import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { addToWishlist, removeFromWishlist } from '../../store/wishlistSlice';
import { setPreviousRoute } from '../../store/navigationSlice';
import { showShopToast } from '../../utils/shopToast';
import './CategoryPage.css';

import ShopNav from '../../components/shopNav/ShopNav';
import Footer from '../../components/footer/footer';
import NavCategorias from '../../components/navbar/NavCategorias';
import ProductFilter from '../../components/filters/ProductFilter';
import { categories } from '../../config/categories';
import ServiceHighlights from '../../components/servicehighlights/ServiceHighlights';
import ProductImageGallery from '../../components/shop/ProductImageGallery';

// ✅ Variable de entorno
const API_URL = import.meta.env.VITE_API_URL;

const formatCOP = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const CategoryProductsPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 15;
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    minPrice: '',
    maxPrice: '',
    sort: ''
  });

  const wishlistItems = useSelector(state => state.wishlist.items || []);
  const isInWishlist = id => wishlistItems.some(item => item.id === id);

  // Obtener subcategorías de la categoría actual
  const currentCategory = useMemo(() => categories.find(cat => cat.id === categoryId), [categoryId]);
  const subcategories = currentCategory ? currentCategory.subcategories : [];
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const categoryName = useMemo(() => categoryId.replace(/-/g, ' '), [categoryId]);

  useEffect(() => {
    dispatch(setPreviousRoute(window.location.pathname));
  }, [dispatch, categoryId]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/destacados/categoria/${categoryId}`);
        setProducts(response.data);
      } catch (err) {
        setError('Error al cargar los productos. Intente nuevamente.');
        console.error('Error fetching category products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categoryId]);

  const filteredProducts = useMemo(() => {
    let result = products.filter(product => {
      const nameMatch = product.name.toLowerCase().includes(filters.search.toLowerCase());
      const minPriceMatch = filters.minPrice === '' || product.priceCOP >= parseFloat(filters.minPrice);
      const maxPriceMatch = filters.maxPrice === '' || product.priceCOP <= parseFloat(filters.maxPrice);
      // Filtrar por subcategoría si está seleccionada
      const subcatMatch = selectedSubcategory === '' || product.subcategory === selectedSubcategory;
      return nameMatch && minPriceMatch && maxPriceMatch && subcatMatch;
    });

    switch (filters.sort) {
      case 'price-asc':
        result.sort((a, b) => a.priceCOP - b.priceCOP);
        break;
      case 'price-desc':
        result.sort((a, b) => b.priceCOP - a.priceCOP);
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }

    return result;
  }, [products, filters, selectedSubcategory]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / productsPerPage));
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = pageNumber => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    dispatch(addToCart(product));
    showShopToast(`${product.name} agregado al carrito`, 'success');
  };

  const handleToggleWishlist = (e, product) => {
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      dispatch(removeFromWishlist(product.id));
      setToastMessage(`${product.name} eliminado de la lista de deseos`);
    } else {
      dispatch(addToWishlist(product));
      setToastMessage(`${product.name} agregado a la lista de deseos`);
    }
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleProductDetails = (id) => {
    navigate(`/producto/${id}`);
  };

  return (
    <div className="category-page">
      <ShopNav />
      <NavCategorias />

      <main className="category-page-main">
        <section className="category-page-hero category-page-elevated-wrap category-page-elevated-wrap--hero">
          <div className="container category-page-hero-inner">
            <div className="category-page-hero-copy">
              <h1 className="display-5 animate__animated animate__fadeIn">{categoryName}</h1>
              <p className="lead animate__animated animate__fadeIn">
                Descubre productos destacados de esta categoría con enfoque profesional para estudio, escenario y producción.
              </p>
            </div>
            <div className="category-page-hero-logo-wrap animate__animated animate__fadeIn" aria-hidden="true">
              <img src="/images/VPA.png" alt="Vintage Pro Audio" className="category-page-hero-logo" />
            </div>
          </div>
        </section>

        <section className="category-page-elevated-wrap">
          <ServiceHighlights />
        </section>

        <section className="category-page-elevated-wrap category-page-filter-wrap">
          <ProductFilter filters={filters} setFilters={setFilters} />
          {subcategories.length > 0 && (
            <div className="container mt-3">
              <div className="row">
                <div className="col-md-5 col-lg-4 mx-auto">
                  <select
                    className="form-select"
                    value={selectedSubcategory}
                    onChange={e => setSelectedSubcategory(e.target.value)}
                  >
                    <option value="">Todas las subcategorías</option>
                    {subcategories.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="container category-page-results">
          {loading ? (
            <div className="category-page-state text-center">Cargando productos...</div>
          ) : error ? (
            <div className="alert alert-danger text-center">{error}</div>
          ) : (
            currentProducts.length === 0 ? (
              <div className="alert alert-info text-center">
                {selectedSubcategory
                  ? 'No se encontraron productos en esta subcategoría.'
                  : 'No se encontraron productos en esta categoría.'}
              </div>
            ) : (
              <>
                <div className="category-page-results-head">
                  <h2 className="category-page-results-title">Productos de {categoryName}</h2>
                  <p className="category-page-results-meta">{filteredProducts.length} producto(s) disponible(s)</p>
                </div>

                <div className="row g-4 category-products-grid">
                  {currentProducts.map(product => {
                    let pdfs = {};

                    try {
                      pdfs = product.pdfUrl ? JSON.parse(product.pdfUrl) : {};
                    } catch {
                      console.warn(`⚠️ No se pudo parsear pdfUrl del producto ID ${product.id}`);
                    }

                    return (
                      <div key={product.id} className="col-12 col-sm-6 col-lg-4 category-product-col" onClick={() => handleProductDetails(product.id)}>
                        <div className="card shadow-sm h-100 category-product-card">
                          <div className="card-body d-flex flex-column">
                            <div className="category-card-wishlist" onClick={(e) => handleToggleWishlist(e, product)} style={{ zIndex: 10 }}>
                              <i className={`bi bi-heart${isInWishlist(product.id) ? '-fill text-danger' : ''}`} style={{ fontSize: '1.5rem' }}></i>
                            </div>

                            <div onClick={(e) => e.stopPropagation()} className="image-gallery-wrapper category-card-media mb-3">
                              <ProductImageGallery
                                imageUrl={product.imageUrl}
                                productName={product.name}
                                showPlayButton={false}
                                showFullscreenButton={false}
                                showThumbnails={false}
                                autoPlay
                              />
                            </div>

                            <div className="category-card-meta">
                              <span className="category-card-chip">{categoryName}</span>
                              <span className="category-card-sku">Ref. {product.id}</span>
                            </div>

                            <h5 className="card-title">{product.name}</h5>
                            <p className="card-text">{product.description}</p>

                            <div className="category-card-pricing">
                              <p className="fw-bold text-success">{formatCOP(product.priceCOP)}</p>
                            </div>

                            <div className="category-card-actions mt-auto" onClick={(e) => e.stopPropagation()}>
                              {pdfs.pdf1 && (
                                <a href={pdfs.pdf1} target="_blank" rel="noopener noreferrer" className="btn btn-primary w-100">Descargar Especificaciones</a>
                              )}

                              <button className="btn btn-success w-100" onClick={(e) => handleAddToCart(e, product)}>
                                Agregar al carrito
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}

          <nav className="mt-4">
            <ul className="pagination justify-content-center category-pagination">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(1)}>&laquo;</button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage - 1)}>&lsaquo;</button>
              </li>
              {[...Array(totalPages).keys()].map(n => (
                <li key={n + 1} className={`page-item ${currentPage === n + 1 ? 'active' : ''}`}>
                  <button onClick={() => paginate(n + 1)} className="page-link">{n + 1}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage + 1)}>&rsaquo;</button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(totalPages)}>&raquo;</button>
              </li>
            </ul>
          </nav>

          <div className="toast-container position-fixed bottom-0 end-0 p-3 shop-toast-container" style={{ zIndex: 9999 }}>
            <div className={`toast align-items-center text-bg-success border-0 ${showToast ? 'show' : 'hide'}`} role="alert">
              <div className="d-flex">
                <div className="toast-body">{toastMessage}</div>
                <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setShowToast(false)}></button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CategoryProductsPage; 